import { ACTION, MixpanelEvent, useMixpanel } from '@talos/kyoko';
import { useCallback } from 'react';
import { useRoleAuth, useTransfersDrawer } from '.';
import { toggleView } from '../components/OMS/OMSSlice';
import { OMSView } from '../components/OMS/OMSView';
import { useAppStateDispatch } from '../providers/AppStateProvider';
import { useOrderEntry } from '../providers/OrderEntryContext';
import { useTradingSettings } from '../providers/TradingSettingsContext';

export function useOrderFormToggle() {
  const { defaultOrderFormTab, enableSalesWorkflow } = useTradingSettings();
  const { isAuthorized } = useRoleAuth();
  const { lastOrderFormUsed } = useOrderEntry();
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { closeTransfersDrawer } = useTransfersDrawer();
  return useCallback(() => {
    const hasPermissionForOmsView = (view: OMSView) => {
      switch (view) {
        case OMSView.NewOrderForm:
          return isAuthorized(ACTION.SUBMIT_ORDER);
        case OMSView.RFQForm:
          return isAuthorized(ACTION.REQUEST_RFQ);
        case OMSView.SalesOrder:
          return isAuthorized(ACTION.SUBMIT_ORDER) && enableSalesWorkflow;
        case OMSView.SalesRFQForm:
          return isAuthorized(ACTION.REQUEST_RFQ) && enableSalesWorkflow;
        default:
          return false;
      }
    };

    const launchDefaultTab = () => {
      if (isAuthorized(ACTION.SUBMIT_ORDER)) {
        dispatch(toggleView(OMSView.NewOrderForm));
      } else {
        dispatch(toggleView(OMSView.RFQForm));
      }
    };
    const tab = defaultOrderFormTab || lastOrderFormUsed;
    mixpanel.track(MixpanelEvent.OpenOrderForm);
    const hasPermissionForLastTab = hasPermissionForOmsView(tab);

    if (!hasPermissionForLastTab) {
      // user lost permission to their preferred tab / last viewed tab - need to open a safe default
      // since Order Forms toggle is visible it means user have at least one of the below permissions
      launchDefaultTab();
      return;
    }
    dispatch(toggleView(tab));
    closeTransfersDrawer();
  }, [
    defaultOrderFormTab,
    dispatch,
    enableSalesWorkflow,
    isAuthorized,
    lastOrderFormUsed,
    mixpanel,
    closeTransfersDrawer,
  ]);
}
